import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Checkbox, Input } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { agentsApi, type AgentInput } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';
import type { Agent } from '@/domain/types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  agent?: Agent | null;
}

interface FormState extends AgentInput {
  isActive: boolean;
  withLogin: boolean;
  username: string;
  password: string;
}

const EMPTY: FormState = {
  name: '',
  contactNumber: '',
  nic: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  isActive: true,
  withLogin: false,
  username: '',
  password: '',
};

export function AgentFormModal({ isOpen, onClose, agent }: Props): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;

    setErrors({});
    setForm(
      agent
        ? {
            ...EMPTY,
            name: agent.name,
            contactNumber: agent.contactNumber ?? '',
            nic: agent.nic ?? '',
            addressLine1: agent.addressLine1 ?? '',
            addressLine2: agent.addressLine2 ?? '',
            addressLine3: agent.addressLine3 ?? '',
            isActive: agent.isActive,
          }
        : EMPTY,
    );
  }, [isOpen, agent]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: AgentInput = {
        name: form.name,
        contactNumber: form.contactNumber || null,
        nic: form.nic || null,
        addressLine1: form.addressLine1 || null,
        addressLine2: form.addressLine2 || null,
        addressLine3: form.addressLine3 || null,
      };

      if (agent) {
        return agentsApi.update(agent.id, { ...payload, isActive: form.isActive });
      }

      // The login is created in the same call, so an agent record and its
      // credential never drift apart.
      return agentsApi.create(
        form.withLogin
          ? { ...payload, login: { username: form.username.trim(), password: form.password } }
          : payload,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      toast.success(agent ? t('agent.updated') : t('agent.created'));
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.fieldErrors.length > 0) {
        setErrors(Object.fromEntries(error.fieldErrors.map((issue) => [issue.field, issue.message])));
      } else {
        toast.error(error instanceof ApiError ? error.message : t('errors.unknown'));
      }
    },
  });

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  const loginIncomplete = form.withLogin && (form.username.trim().length < 3 || form.password.length < 8);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={agent ? t('agent.edit') : t('agent.new')}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            isLoading={mutation.isPending}
            disabled={!form.name.trim() || loginIncomplete}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label={t('agent.name')}
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          error={errors.name}
          required
          autoFocus
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('agent.contactNumber')}
            value={form.contactNumber ?? ''}
            onChange={(event) => update('contactNumber', event.target.value)}
            inputMode="tel"
          />
          <Input
            label={t('agent.nic')}
            value={form.nic ?? ''}
            onChange={(event) => update('nic', event.target.value)}
          />
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-700">{t('agent.address')}</p>
          {([1, 2, 3] as const).map((line) => (
            <Input
              key={line}
              placeholder={`${t('agent.address')} ${line}`}
              value={(form[`addressLine${line}` as keyof FormState] as string) ?? ''}
              onChange={(event) =>
                update(`addressLine${line}` as keyof FormState, event.target.value as never)
              }
            />
          ))}
        </div>

        {agent ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <Checkbox
              label={t('common.active')}
              checked={form.isActive}
              onChange={(event) => update('isActive', event.target.checked)}
            />
            {!form.isActive && (
              <p className="mt-2 text-xs text-amber-700">{t('agent.deactivateWarning')}</p>
            )}
          </div>
        ) : (
          <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <Checkbox
              label={t('agent.createLogin')}
              checked={form.withLogin}
              onChange={(event) => update('withLogin', event.target.checked)}
            />
            <p className="text-xs text-slate-500">{t('agent.loginHelp')}</p>

            {form.withLogin && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label={t('auth.username')}
                  value={form.username}
                  onChange={(event) => update('username', event.target.value)}
                  error={errors['login.username']}
                  autoComplete="off"
                  required
                />
                <Input
                  label={t('auth.password')}
                  type="password"
                  value={form.password}
                  onChange={(event) => update('password', event.target.value)}
                  error={errors['login.password']}
                  hint={t('auth.passwordRules')}
                  autoComplete="new-password"
                  required
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
